import { Center, Stack, useColorModeValue, Text, Badge, Icon, Tooltip, useDisclosure } from "@chakra-ui/react";
import type { FunctionComponent } from "react";
import { useMemo, useContext } from "react";
import React from "react";
import { IntentIcon } from "screens/common/components";
import { ConversationContext } from "screens/thread/ConversationContext";
import type { WorkflowTaskStatus } from "types/workflows/workflow";
import { ActivityActionButtons } from "./ActivityActionButtons";
import { useWorkflowCompletionDateOrNow, useWorkflowKey } from "hooks/useWorkflows";
import { WorkflowProgressBar } from "screens/landing/components/WorkflowProgressBar";
import { formatDistanceToNow, differenceInMinutes, differenceInSeconds } from "date-fns";
import { TextOverflowTooltip } from "screens/landing/components/TextOverflowTooltip";
import { useConversation } from "hooks";
import { capitalize } from "lodash";
import { getStatusColor } from "screens/common/components/WorkflowStatusIcon";
import { FcWorkflow } from "react-icons/fc";
import { MessagesViewJsonPanel } from "screens/thread/components/MessagesViewJsonPanel";
import { BsFiletypeJson } from "react-icons/bs";

interface Props {
  conversationId: string;
  initiatingMessage?: string;
  reply?: string;
  lastUpdated: Date;
  state: WorkflowTaskStatus;
  intent: string;
  selected?: boolean;
  dismissed?: boolean;
  hideStatusIcon?: boolean;
  tileLocation: "landing" | "activity_list_drawer";
  workflowId?: string;
  showAsList?: boolean;
  handleViewWorkflow?: () => void;
}

export const ActivityTile: FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<Props>>> = (props: Props) => {
  const tileBorderColor = useColorModeValue("#cbd5e0", "#2D3748");
  const { setConversationId, onConversationOpen, setShowConversationMessages } = useContext(ConversationContext);
  const currentWorkflowCompletionDate = useWorkflowCompletionDateOrNow(props.workflowId);
  const tileCreationDate = useWorkflowKey(props.workflowId, "creationDate");
  const titleColor = useColorModeValue("charli.lightGray", "gray.500");
  const textColor = useColorModeValue("primary.darkGray", "gray.400");
  const { isOpen, onOpen, onClose } = useDisclosure();

  const isWorkflowInProgress = useMemo(() => {
    return props.state === "in_progress" || props.state === "clarification_needed" || props.state === "queued";
  }, [props.state]);

  const duration = useMemo(() => {
    if (!tileCreationDate) return null;

    const endDate = isWorkflowInProgress ? new Date() : new Date(currentWorkflowCompletionDate);
    if (!isWorkflowInProgress && !currentWorkflowCompletionDate) return null;

    const minutes = differenceInMinutes(endDate, new Date(tileCreationDate));
    if (minutes === 0) {
      const seconds = differenceInSeconds(endDate, new Date(tileCreationDate));
      if (seconds === 0) return { value: "<1", unit: "sec" };
      return { value: seconds, unit: seconds === 1 ? "sec" : "secs" };
    }
    return { value: minutes, unit: minutes === 1 ? "min" : "mins" };
  }, [tileCreationDate, currentWorkflowCompletionDate, isWorkflowInProgress]);
  const { inlineActions } = useConversation(props.conversationId);
  const hasActionState = props.state === "in_progress" || props.state === "clarification_needed" || props.state === "error";

  return (
    <>
      <Stack
        className="ch-activity-tile"
        cursor={"pointer"}
        onClick={() => {
          setShowConversationMessages(true);
          setConversationId(props.conversationId);
          onConversationOpen();
        }}
        direction="row"
        spacing="1rem"
        height="3rem"
        width="100%"
        justifyContent={"space-between"}
        borderBottom={`1px solid ${tileBorderColor}`}>
        <Stack direction="row" spacing="1rem" width="100%" maxWidth={"20rem"}>
          <Center>
            <IntentIcon intent={props.intent} />
          </Center>
          <Center>
            <TextOverflowTooltip
              style={{ width: "100%" }}
              label={props.intent === "find" ? props.initiatingMessage?.replace("Find", "Search") || "" : props.initiatingMessage || ""}
              className="ch-activity-title-initial-message"
            />
          </Center>
        </Stack>
        <Stack direction="row" spacing="1rem" width="100%">
          <Stack direction="row" spacing="1rem" width="100%" justifyContent={"right"} align={"center"}>
            <Stack direction={"row"} spacing="1rem" width="100%">
              {props.workflowId && isWorkflowInProgress ? (
                <Center pb="0" width="100%">
                  <WorkflowProgressBar
                    maxWidth="100%"
                    workflowId={props.workflowId}
                    lastUpdated={formatDistanceToNow(new Date(currentWorkflowCompletionDate), {
                      addSuffix: true,
                      includeSeconds: false,
                    })}
                    hideCompletedInDate
                    size="compact"
                  />
                </Center>
              ) : (
                <TextOverflowTooltip style={{ width: "100%" }} label={props.reply || ""} className="ch-activity-title-reply" />
              )}
              <Stack direction="row" spacing="1rem" width="100%" maxWidth={"12.5rem"} justifyContent={"flex-end"}>
                <Center>
                  <Badge colorScheme={getStatusColor(props.state)}>
                    <TextOverflowTooltip
                      label={capitalize(props.state).split("_").join(" ")}
                      tooltip={capitalize(props.state).split("_").join(" ")}
                      fontSize="xs"
                      noOfLines={1}
                      breakWord={false}
                      hideTooltip={false}
                      style={{ width: "100%" }}
                      color={titleColor}
                    />
                  </Badge>
                </Center>
                {props.handleViewWorkflow && (
                  <Tooltip label="Open Workflow" placement="top">
                    <Center>
                      <Icon
                        as={FcWorkflow}
                        boxSize="1.4rem"
                        onClick={(event) => {
                          event.stopPropagation();
                          props.handleViewWorkflow && props.handleViewWorkflow();
                        }}
                      />
                    </Center>
                  </Tooltip>
                )}
                <Tooltip label="Open Conversation JSON" placement="top">
                  <Center>
                    <Icon
                      pt="2px"
                      color={textColor}
                      as={BsFiletypeJson}
                      boxSize="1.3rem"
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpen();
                      }}
                    />
                  </Center>
                </Tooltip>
              </Stack>
            </Stack>

            {(inlineActions || hasActionState) && !isWorkflowInProgress && (
              <Center>
                <Stack direction="row" justifyContent="flex-end">
                  <ActivityActionButtons {...props} hideActionButtons />
                </Stack>
              </Center>
            )}
          </Stack>
          <Center width={"5rem"}>
            <Tooltip label="Time Elapsed" placement="top">
              <Text fontSize={"sm"} color={textColor} textAlign={"right"} width={"5rem"}>
                {duration !== null ? `${duration.value} ${duration.unit}` : props.state}
              </Text>
            </Tooltip>
          </Center>
          <Center>
            <TextOverflowTooltip
              style={{ width: "8rem", textAlign: "right" }}
              label={
                tileCreationDate
                  ? new Date(tileCreationDate).toLocaleDateString(undefined, {
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : ""
              }
              className="ch-activity-title"
            />
          </Center>
        </Stack>
      </Stack>
      {isOpen && (
        <MessagesViewJsonPanel title="Conversation JSON" conversationId={props.conversationId} isOpen={isOpen} onClose={onClose} />
      )}
    </>
  );
};
